/**
 * Data for the file dialogs — the workspaces a user may browse, a workspace's
 * folders, and the paged file listing. React Query throughout: the listing is
 * server-searched and server-paged (`GET /vault/documents/picker`), so a Vault
 * of any size browses without loading into the browser.
 */
import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
  api,
  type VaultDocumentFolderDto,
  type VaultFileDialogArea,
  type VaultFileDialogDocumentDto,
  type VaultFileKindDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { mimeToFileKind } from '@/lib/vault-file-kinds';

/** A workspace the dialog can browse. */
export interface FileDialogWorkspace {
  holderId: string;
  label: string;
  holderType: 'personal' | 'org' | 'guild';
  role: 'owner' | 'admin' | 'member' | 'viewer';
}

const WRITE_ROLES = new Set(['owner', 'admin', 'member']);

/**
 * The workspaces the user may browse: Personal first, then organisations and
 * guild spaces. `write` keeps only those they can save into (member or
 * above); an Open lists every one they can read.
 */
export function useFileDialogWorkspaces(need: 'read' | 'write') {
  const query = useQuery({
    queryKey: queryKeys.vault.me.memberships(),
    queryFn: () => api.listMyVaultMemberships(),
  });
  const workspaces = useMemo<FileDialogWorkspace[]>(() => {
    const data = query.data;
    if (!data) return [];
    const out: FileDialogWorkspace[] = [];
    if (data.personalHolder) {
      out.push({
        holderId: data.personalHolder.holderId,
        label: 'Personal',
        holderType: 'personal',
        role: 'owner',
      });
    }
    for (const m of data.memberships) {
      if (m.status !== 'active' || m.holderType !== 'org') continue;
      out.push({
        holderId: m.holderId,
        label: m.holderDisplayName,
        holderType: 'org',
        role: m.role,
      });
    }
    for (const g of data.guildSpaces ?? []) {
      out.push({
        holderId: g.holderId,
        label: g.holderDisplayName,
        holderType: 'guild',
        role: g.role,
      });
    }
    return need === 'write' ? out.filter((w) => WRITE_ROLES.has(w.role)) : out;
  }, [query.data, need]);
  return {
    workspaces,
    /** Who is browsing — keys the per-user last-location memory. */
    principalId: query.data?.principalId ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Every folder of a workspace, flat (the tree is assembled by callers). */
export function useFileDialogFolders(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.folders(holderId ?? ''),
    queryFn: () => api.listVaultDocumentFolders(holderId!),
    select: (data): VaultDocumentFolderDto[] => data.folders,
    enabled: !!holderId,
  });
}

export const FILE_DIALOG_PAGE_SIZE = 60;

export interface FileDialogListingQuery {
  holderId: string | null;
  areas: readonly VaultFileDialogArea[];
  /** Browse one folder (`null` = the Holder root). Omit to span the areas. */
  folderId?: string | null;
  search?: string;
  kinds?: readonly VaultFileKindDto[];
  sort: 'updated' | 'name' | 'size' | 'kind';
  direction: 'asc' | 'desc';
  /** A pick whose result an arche receives — never proprietary rows. */
  forArche?: boolean;
  enabled?: boolean;
}

/** The paged listing. `files` is every page loaded so far; `total` is the
 *  server's count for the whole query. */
export function useFileDialogListing(q: FileDialogListingQuery) {
  const key = {
    areas: [...q.areas],
    ...(q.folderId !== undefined ? { folderId: q.folderId } : {}),
    ...(q.search ? { search: q.search } : {}),
    ...(q.kinds?.length ? { kinds: [...q.kinds] } : {}),
    sort: q.sort,
    direction: q.direction,
    ...(q.forArche ? { forArche: true } : {}),
  };
  const query = useInfiniteQuery({
    queryKey: queryKeys.vaultDocuments.fileDialog(q.holderId ?? '', key),
    enabled: !!q.holderId && q.enabled !== false,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      api.listVaultFileDialogDocuments(q.holderId!, {
        areas: [...q.areas],
        folderId: q.folderId,
        search: q.search || undefined,
        kinds: q.kinds?.length ? [...q.kinds] : undefined,
        sort: q.sort,
        direction: q.direction,
        forArche: q.forArche,
        limit: FILE_DIALOG_PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, page) => n + page.documents.length, 0);
      return loaded < last.total && last.documents.length > 0 ? loaded : undefined;
    },
  });
  const files = useMemo<VaultFileDialogDocumentDto[]>(
    () => query.data?.pages.flatMap((page) => page.documents) ?? [],
    [query.data],
  );
  const total = query.data?.pages[0]?.total ?? 0;
  return {
    files,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasMore: query.hasNextPage === true,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/** A value that settles `delayMs` after it last changed — the search box. */
export function useDebounced<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

const MAX_THUMBNAILS = 120;

/**
 * Presigned thumbnail URLs for the image rows on screen, fetched lazily and
 * progressively (one presign per image, never on the list read path). Capped,
 * so a folder of thousands of photos cannot fan out thousands of requests.
 */
export function useThumbnails(files: readonly VaultFileDialogDocumentDto[]): Map<string, string> {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    const wanted = files
      .filter((f) => mimeToFileKind(f.mimeType) === 'image' && !urls.has(f.documentId))
      .slice(0, Math.max(0, MAX_THUMBNAILS - urls.size));
    if (wanted.length === 0) return;
    let cancelled = false;
    void Promise.allSettled(
      wanted.map(async (file) => {
        const { url } = await api.getVaultDocumentDownloadUrl(file.documentId);
        if (cancelled) return;
        setUrls((prev) => {
          if (prev.has(file.documentId)) return prev;
          const next = new Map(prev);
          next.set(file.documentId, url);
          return next;
        });
      }),
    );
    return () => {
      cancelled = true;
    };
    // `urls` is read only to skip what is already fetched; re-running on its
    // every change would re-request the in-flight set, so it is not a dep.
  }, [files]);
  return urls;
}
